import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import apiConfig, { safeFetch } from '../config/api';

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// SELF-PROTECTING API URL
// Uses centralized config that enforces same-origin in production
// See /config/api.js for protection rules
export const API_URL = apiConfig.API_BASE;

// Validate API response has data (failsafe against empty responses)
const validateResponse = (data, endpoint) => {
  if (Array.isArray(data) && data.length === 0) {
    console.warn(`[API Warning] ${endpoint} returned empty array - possible data issue`);
  }
  return data;
};

// ============================================================
// API METHODS WITH AUTO-RECOVERY
// Uses safeFetch for automatic retry + same-origin fallback
// ============================================================

export const api = {
  get: async (endpoint) => {
    const response = await safeFetch(`/api${endpoint}`);
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    const data = await response.json();
    return validateResponse(data, endpoint);
  },
  
  post: async (endpoint, data, token = null) => {
    const headers = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    
    const response = await safeFetch(`/api${endpoint}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    return response.json();
  },
  
  put: async (endpoint, data, token) => {
    const response = await safeFetch(`/api${endpoint}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    return response.json();
  },
  
  delete: async (endpoint, token) => {
    const response = await safeFetch(`/api${endpoint}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    return response.json();
  },
};

export const formatCurrency = (amount, currency = 'USD') => {
  // Handle NaN, undefined, null values
  const safeAmount = Number(amount);
  if (isNaN(safeAmount) || safeAmount === null || safeAmount === undefined) {
    return '$0';
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(safeAmount);
};

export const calculateNomadScore = (scores) => {
  const { cost_score, tax_score, visa_score, safety_score, internet_score, lifestyle_score } = scores;
  return (
    cost_score * 0.25 +
    tax_score * 0.20 +
    visa_score * 0.15 +
    safety_score * 0.15 +
    internet_score * 0.15 +
    lifestyle_score * 0.10
  ).toFixed(1);
};

export const getScoreColor = (score) => {
  if (score >= 8) return 'bg-emerald-500';
  if (score >= 6) return 'bg-blue-500';
  if (score >= 4) return 'bg-amber-500';
  return 'bg-red-500';
};

export const getScoreLabel = (score) => {
  if (score >= 8) return 'Excellent';
  if (score >= 6) return 'Good';
  if (score >= 4) return 'Average';
  return 'Below Average';
};
